/**
 * PreviewPage.jsx — Track B, Phase 3 (hidden preview mount).
 *
 * Mounted at /customer-intelligence-preview via index.js. Auth-gated:
 * only admin/ceo can view. This page does NOT replace any existing
 * customer screen — it is a visual-review sandbox for the new
 * Customer Intelligence components.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import SegmentTimeline from "./SegmentTimeline";
import MovementMap from "./MovementMap";
import CampaignCard from "./CampaignCard";

export default function PreviewPage() {
  const [auth, setAuth] = useState({ loading: true, user: null, error: null });
  const [windowDays, setWindowDays] = useState(90);
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setAuth({ loading: false, user: r.data, error: null }))
      .catch((e) => setAuth({
        loading: false,
        user: null,
        error: e?.response?.status === 401 ? "unauthenticated" : (e?.message || "خطأ"),
      }));
  }, []);

  useEffect(() => {
    if (!auth.user) return;
    api.get("/pos/branches").then((r) => {
      const rows = r.data?.branches || r.data || [];
      if (Array.isArray(rows)) setBranches(rows);
    }).catch(() => { /* branches list is optional — silent */ });
  }, [auth.user]);

  const role = auth.user?.role;
  const isAllowed = role === "admin" || role === "ceo";

  return (
    <div
      dir="rtl"
      data-testid="ci-preview-page"
      style={{
        minHeight: "100vh",
        background: "radial-gradient(ellipse at top, #1C1C24 0%, #0A0A10 60%)",
        color: "#F5F5F7",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Noto Naskh Arabic', sans-serif",
        padding: "40px 24px",
      }}
    >
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <header style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span style={{
              padding: "4px 10px", borderRadius: 999,
              background: "rgba(255,149,0,0.16)", color: "#FF9F0A",
              fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
            }}>PREVIEW</span>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: -0.6 }}>
              ذكاء العملاء
            </h1>
          </div>
          <p style={{ marginTop: 10, fontSize: 14, color: "#8E8E93" }}>
            صندوق معاينة معزول — لا يستبدل شاشات الإنتاج الحالية.
          </p>
        </header>

        {auth.loading && (
          <div data-testid="ci-preview-loading" style={{ padding: 40, textAlign: "center", color: "#8E8E93" }}>
            جارٍ التحقق من الصلاحية…
          </div>
        )}

        {!auth.loading && auth.error === "unauthenticated" && (
          <div
            data-testid="ci-preview-unauth"
            style={{
              padding: 24, borderRadius: 16,
              background: "rgba(255,59,48,0.08)", border: "1px solid rgba(255,59,48,0.24)",
              color: "#FF9F0A",
            }}
          >
            يجب تسجيل الدخول أولًا. افتح <code style={{ background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4 }}>/</code> وسجّل ثم عُد إلى هذه الصفحة.
          </div>
        )}

        {!auth.loading && auth.user && !isAllowed && (
          <div
            data-testid="ci-preview-forbidden"
            style={{
              padding: 24, borderRadius: 16,
              background: "rgba(255,59,48,0.08)", border: "1px solid rgba(255,59,48,0.24)",
              color: "#FF453A",
            }}
          >
            هذه الصفحة مخصّصة لدور <b>admin</b> أو <b>ceo</b> فقط.
            (دورك الحالي: {role || "غير معروف"})
          </div>
        )}

        {!auth.loading && isAllowed && (
          <>
            <div
              data-testid="ci-preview-controls"
              style={{
                display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
                marginBottom: 24, padding: 16,
                background: "rgba(255,255,255,0.03)",
                borderRadius: 14, border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                نافذة زمنية
                <select
                  data-testid="ci-preview-window"
                  value={windowDays}
                  onChange={(e) => setWindowDays(Number(e.target.value))}
                  style={{
                    background: "rgba(255,255,255,0.06)", color: "#F5F5F7",
                    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
                    padding: "6px 10px", fontSize: 13,
                  }}
                >
                  <option value={30}>30 يوم</option>
                  <option value={60}>60 يوم</option>
                  <option value={90}>90 يوم</option>
                  <option value={180}>180 يوم</option>
                </select>
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                الفرع
                <select
                  data-testid="ci-preview-branch"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  style={{
                    background: "rgba(255,255,255,0.06)", color: "#F5F5F7",
                    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
                    padding: "6px 10px", fontSize: 13, minWidth: 140,
                  }}
                >
                  <option value="">كل الفروع</option>
                  {branches.map((b) => (
                    <option key={b.id || b._id || b.name} value={b.id || b._id || b.name}>
                      {b.name_ar || b.name || b.id || b._id}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <SegmentTimeline
              branchId={branchId || null}
              windowDays={windowDays}
            />

            <MovementMap />

            <section
              data-testid="ci-preview-campaign-cards"
              style={{
                marginTop: 24, display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 14,
              }}
            >
              {["gold_vip", "rising_star", "loyal", "at_risk",
                "promising", "new", "regular", "dormant",
                "drifter", "lost"].map((code) => (
                <CampaignCard
                  key={code}
                  segmentCode={code}
                  count={0}
                  percentage={0}
                />
              ))}
            </section>
          </>
        )}

        <footer
          style={{
            marginTop: 40, paddingTop: 20,
            borderTop: "1px solid rgba(255,255,255,0.06)",
            fontSize: 12, color: "#48484A", textAlign: "center",
          }}
        >
          Track B · Phase 3 · مسار معاينة مخفي — غير مرتبط بشاشة إنتاج
        </footer>
      </div>
    </div>
  );
}
